import { splitProportionally } from "@creators/backend/lib/money";
import type { MilestoneTrigger } from "./state-machine";

export type ScheduleItem = { trigger: MilestoneTrigger; pct: number };

export const TRIGGER_ORDER: readonly MilestoneTrigger[] = [
  "on_accept",
  "on_draft_approval",
  "on_publish",
  "on_live_period_end",
];

export const TRIGGER_LABELS: Record<MilestoneTrigger, string> = {
  on_accept: "On acceptance",
  on_draft_approval: "On draft approval",
  on_publish: "On publishing",
  on_live_period_end: "At end of live period",
};

export type SchedulePresetKey = "three_stage" | "advance_balance" | "on_approval";

export const SCHEDULE_PRESETS: Record<
  SchedulePresetKey,
  { label: string; description: string; schedule: ScheduleItem[] }
> = {
  three_stage: {
    label: "3-stage",
    description: "25% up front, remaining 75% when work is approved (paid together).",
    schedule: [
      { trigger: "on_accept", pct: 25 },
      { trigger: "on_draft_approval", pct: 25 },
      { trigger: "on_publish", pct: 50 },
    ],
  },
  advance_balance: {
    label: "Advance",
    description: "A 25% commitment up front, the rest on draft approval.",
    schedule: [
      { trigger: "on_accept", pct: 25 },
      { trigger: "on_draft_approval", pct: 75 },
    ],
  },
  on_approval: {
    label: "On approval",
    description: "Nothing up front — the full amount once you approve the draft.",
    schedule: [{ trigger: "on_draft_approval", pct: 100 }],
  },
};

export const DEFAULT_PRESET: SchedulePresetKey = "three_stage";

/** Stable compare key — ignores key order / stringified pcts from JSONB. */
export function scheduleFingerprint(
  schedule: Array<{ trigger: string; pct: number | string }>,
): string {
  return [...schedule]
    .map((item) => ({ trigger: String(item.trigger), pct: Number(item.pct) }))
    .filter((item) => item.trigger && Number.isFinite(item.pct))
    .sort((a, b) => TRIGGER_ORDER.indexOf(a.trigger as MilestoneTrigger) - TRIGGER_ORDER.indexOf(b.trigger as MilestoneTrigger))
    .map((item) => `${item.trigger}:${item.pct}`)
    .join("|");
}

/** Which named preset matches this offer schedule (falls back to default). */
export function presetKeyForSchedule(
  schedule: Array<{ trigger: string; pct: number | string }> | null | undefined,
): SchedulePresetKey {
  if (!schedule?.length) return DEFAULT_PRESET;
  const target = scheduleFingerprint(schedule);
  return (
    (Object.keys(SCHEDULE_PRESETS) as SchedulePresetKey[]).find(
      (key) => scheduleFingerprint(SCHEDULE_PRESETS[key].schedule) === target,
    ) ?? DEFAULT_PRESET
  );
}

export class InvalidScheduleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidScheduleError";
  }
}

/** Parses and validates a schedule coming from a form or from a JSONB column. */
export function parseSchedule(value: unknown): ScheduleItem[] {
  if (!Array.isArray(value) || value.length === 0) {
    throw new InvalidScheduleError("A payment schedule needs at least one milestone.");
  }

  const items = value.map((raw) => {
    const item = raw as { trigger?: unknown; pct?: unknown };
    const trigger = item.trigger as MilestoneTrigger;
    const pct = Number(item.pct);

    if (!TRIGGER_ORDER.includes(trigger)) {
      throw new InvalidScheduleError(`Unknown milestone trigger: ${String(item.trigger)}`);
    }
    if (!Number.isInteger(pct) || pct <= 0 || pct > 100) {
      throw new InvalidScheduleError(
        "Each milestone must be a whole percentage between 1 and 100.",
      );
    }
    return { trigger, pct };
  });

  const total = items.reduce((sum, i) => sum + i.pct, 0);
  if (total !== 100) {
    throw new InvalidScheduleError(`Milestone percentages must add up to 100, got ${total}.`);
  }

  const seen = new Set<MilestoneTrigger>();
  for (const item of items) {
    if (seen.has(item.trigger)) {
      throw new InvalidScheduleError(`Duplicate milestone trigger: ${item.trigger}`);
    }
    seen.add(item.trigger);
  }

  return sortSchedule(items);
}

export function sortSchedule(items: ScheduleItem[]): ScheduleItem[] {
  return [...items].sort(
    (a, b) => TRIGGER_ORDER.indexOf(a.trigger) - TRIGGER_ORDER.indexOf(b.trigger),
  );
}

export function advancePct(schedule: ScheduleItem[]): number {
  return schedule.find((i) => i.trigger === "on_accept")?.pct ?? 0;
}

export type PlannedMilestone = {
  sequence: number;
  trigger: MilestoneTrigger;
  pct: number;
  amount: number;
};

/**
 * Turns an offer's schedule into concrete milestone rows.
 *
 * The remainder is pushed to the last slice, so the tranches always sum to
 * exactly `amountTotal` — the payer is never asked for a rupee more or less than
 * was agreed.
 */
export function planMilestones(input: {
  amountTotal: number;
  schedule: ScheduleItem[];
}): PlannedMilestone[] {
  const schedule = sortSchedule(input.schedule);
  const amounts = splitProportionally(
    input.amountTotal,
    schedule.map((i) => i.pct),
  );

  return schedule.map((item, index) => ({
    sequence: index + 1,
    trigger: item.trigger,
    pct: item.pct,
    amount: amounts[index],
  }));
}
